import { useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, Play, XCircle } from "lucide-react";

import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import { FLASHPAY_ROLES, RequireRole } from "@/core/auth";
import { LoadScreen, PageBackButton } from "@/core/components/shared";
import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/core/components/ui/tooltip";
import { useSessionStore } from "@/core/stores/session.store";
import {
  notifSilence,
  notifSilenceError,
  notifSilenceWarning,
} from "@/core/utils/func/notifApp";
import type { ApprovalRequestDto } from "@/features/admin/api/dto";
import {
  useApproveApprovalRequest,
  useApprovalRequest,
  useCancelApprovalRequest,
  useMarkApprovalApplied,
  useMarkApprovalFailed,
  useRejectApprovalRequest,
} from "@/features/admin/api";
import {
  ApprovalRequestDecidePreview,
  ApprovalRequestDetailCard,
  ApprovalRequestRejectDialog,
} from "@/features/admin/components";
import { MutationConfirmPreview } from "@/core/components/shared/preview-action";
import { useAdminTranslation } from "@/features/admin/i18n/use-admin-translation";
import { useActivateFeeRule } from "@/features/fee-engine/api/use-activate-fee-rule";
import { useRecordSanctionsScreening } from "@/features/kyc/api/use-record-sanctions-screening";
import { useReverseJournalEntry } from "@/features/ledger/api/use-reverse-journal-entry";
import { useIssueEmoney } from "@/features/wallet/api/use-issue-emoney";
import { useRedeemEmoney } from "@/features/wallet/api/use-redeem-emoney";
import type { EmoneyIssuanceRequestDto } from "@/features/wallet/api/dto";
import type { SanctionsScreeningStatus } from "@/features/shared/api/enums";

/** Action protégée inconnue du dispatch front (aucune actuellement câblée). */
class UnsupportedApprovalActionError extends Error {}

/**
 * Détail d'une demande d'approbation (`GET /ApprovalRequests/{id}`, §6.8).
 *
 * Chaîne maker-checker (guide §5.4) : à l'approbation, le front exécute
 * l'action protégée réelle avec `approvalRequestId` (dispatch par
 * `actionType` — composition multi-features autorisée au niveau route),
 * puis acquitte `mark-applied` (ou `mark-failed` si l'action échoue).
 * Règle des quatre yeux : l'approbateur doit être distinct du demandeur
 * (confirm désactivé + avertissement ; le backend renvoie 422 sinon).
 */
export function ApprovalRequestDetailPage() {
  const { t } = useAdminTranslation();
  const { approvalRequestId = "" } = useParams();
  const {
    data: request,
    isLoading,
    error,
    refetch,
  } = useApprovalRequest(approvalRequestId);

  const approve = useApproveApprovalRequest();
  const reject = useRejectApprovalRequest();
  const cancel = useCancelApprovalRequest();
  const markApplied = useMarkApprovalApplied();
  const markFailed = useMarkApprovalFailed();

  // Exécuteurs des actions protégées (phases 8 + 13 : 5 actions du guide).
  const activateFeeRule = useActivateFeeRule();
  const recordScreening = useRecordSanctionsScreening(request?.resourceId ?? "");
  const reverseJournalEntry = useReverseJournalEntry();
  const issueEmoney = useIssueEmoney();
  const redeemEmoney = useRedeemEmoney();

  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [retryOpen, setRetryOpen] = useState(false);
  const backButton = (
    <PageBackButton to="/app/admin/approvals" label={t("common.back")} />
  );

  if (isLoading) {
    return (
      <section className="flex flex-col gap-4">
        {backButton}
        <div className="flex h-64 items-center justify-center">
          <LoadScreen />
        </div>
      </section>
    );
  }

  if (error || !request) {
    return (
      <section className="flex flex-col gap-4">
        {backButton}
        <div className="flex flex-col items-center gap-4 py-24">
          <p className="font-semibold text-destructive">
            {error?.message ?? t("error")}
          </p>
          <Button variant="secondary" size="sm" onClick={() => void refetch()}>
            {t("reload")}
          </Button>
        </div>
      </section>
    );
  }

  // Créateur = `requestedByKeycloakId` (Keycloak sub, guide §6.8 / Postman).
  // Comparaison stricte au `sub` de session — pas de partyId.
  const currentUserId = useSessionStore.getState().userInfo?.sub;
  const requesterId =
    typeof request.requestedByKeycloakId === "string"
      ? request.requestedByKeycloakId.trim()
      : "";
  const selfApproval =
    typeof currentUserId === "string" &&
    currentUserId.length > 0 &&
    requesterId.length > 0 &&
    currentUserId === requesterId;
  const isPending = request.status === "PENDING";
  // APPROVED sans appliedAt : exécution encore à faire (ex. action non
  // câblée lors de l'approbation — le bouton permet de rejouer).
  const executionPending = request.status === "APPROVED" && !request.appliedAt;

  /** Exécute l'action protégée réelle selon le `actionType` (guide §5.4). */
  const executeProtectedAction = async (req: ApprovalRequestDto) => {
    if (req.actionType === "FEE_RULE_CHANGE") {
      await activateFeeRule.mutateAsync({
        feeRuleId: req.resourceId,
        approvalRequestId: req.id,
      });
      return;
    }
    if (req.actionType === "KYC_FORCE_OVERRIDE") {
      // Payload attendu : {"status": "CLEAR"|..., "reference": "..."}.
      let payload: { status?: string; reference?: string } = {};
      try {
        payload = JSON.parse(req.payload) as typeof payload;
      } catch {
        /* payload non-JSON : le serveur rejettera avec un message clair */
      }
      await recordScreening.mutateAsync({
        status: (payload.status ?? "CLEAR") as SanctionsScreeningStatus,
        reference: payload.reference,
        approvalRequestId: req.id,
      });
      return;
    }
    if (req.actionType === "LEDGER_MANUAL_REVERSAL") {
      // Corps de /reverse : reason = motif de la demande, approbateur =
      // utilisateur courant (quatre yeux déjà garanti par approve).
      const approverId = useSessionStore.getState().userInfo?.sub;
      if (typeof approverId !== "string" || !approverId) {
        throw new Error("Approver Keycloak id is missing from the session.");
      }
      await reverseJournalEntry.mutateAsync({
        journalEntryId: req.resourceId,
        body: {
          reason: req.reason,
          approvedByKeycloakId: approverId,
          approvalRequestId: req.id,
        },
      });
      return;
    }
    if (req.actionType === "EMONEY_ISSUANCE" || req.actionType === "EMONEY_REDEMPTION") {
      let payload: {
        amountMinor?: number;
        currency?: string;
        bankReference?: string;
      } = {};
      try {
        payload = JSON.parse(req.payload) as typeof payload;
      } catch {
        /* payload non-JSON : le serveur rejettera */
      }
      const body: EmoneyIssuanceRequestDto = {
        approvalRequestId: req.id,
        walletId: req.resourceId,
        amountMinor: Number(payload.amountMinor),
        currency: payload.currency ?? "GNF",
        bankReference: payload.bankReference ?? "",
      };
      if (req.actionType === "EMONEY_ISSUANCE") {
        await issueEmoney.mutateAsync(body);
      } else {
        await redeemEmoney.mutateAsync(body);
      }
      return;
    }
    throw new UnsupportedApprovalActionError(req.actionType);
  };

  /** Exécute l'action puis acquitte (APPLIED) ou signale l'échec (FAILED). */
  const executeAndSettle = async (req: ApprovalRequestDto) => {
    try {
      await executeProtectedAction(req);
      // Le service concerné acquitte aussi de son côté (§5.4) : un éventuel
      // 403 de mark-applied (rôles superadmin/service) n'est pas bloquant.
      await markApplied
        .mutateAsync(req.id)
        .catch(() => undefined);
      notifSilence(t("admin.approvals.feedback.approved"));
    } catch (actionError) {
      if (actionError instanceof UnsupportedApprovalActionError) {
        notifSilenceWarning(t("admin.approvals.errors.unsupportedAction"));
        return;
      }
      const message =
        getBackendErrorMessage(actionError) ??
        apiErrorHandler(actionError);
      await markFailed
        .mutateAsync({ approvalRequestId: req.id, failureReason: message })
        .catch(() => undefined);
      notifSilenceError(message);
    }
  };

  const handleApprove = async () => {
    if (typeof currentUserId !== "string" || !currentUserId) {
      notifSilenceError(t("error"));
      return;
    }
    setBusy(true);
    try {
      await approve.mutateAsync({
        approvalRequestId: request.id,
        approverKeycloakId: currentUserId,
      });
      await executeAndSettle(request);
      setApproveOpen(false);
    } catch (approveError) {
      // 422 auto-approbation et autres règles métier : message backend brut.
      notifSilenceError(
        getBackendErrorMessage(approveError) ?? apiErrorHandler(approveError)
      );
    } finally {
      setBusy(false);
    }
  };

  const handleReject = (rejectionReason: string) => {
    if (typeof currentUserId !== "string" || !currentUserId) {
      notifSilenceError(t("error"));
      return;
    }
    reject.mutate(
      {
        approvalRequestId: request.id,
        approverKeycloakId: currentUserId,
        rejectionReason,
      },
      {
        onSuccess: () => {
          notifSilence(t("admin.approvals.feedback.rejected"));
          setRejectOpen(false);
        },
        onError: (mutationError) => notifSilenceError(mutationError.message),
      }
    );
  };

  const handleCancel = () => {
    cancel.mutate(
      {
        approvalRequestId: request.id,
        requesterKeycloakId:
          typeof currentUserId === "string" ? currentUserId : undefined,
      },
      {
        onSuccess: () => {
          notifSilence(t("admin.approvals.feedback.cancelled"));
          setCancelOpen(false);
        },
        onError: (mutationError) => notifSilenceError(mutationError.message),
      }
    );
  };

  const handleRetryExecution = async () => {
    setBusy(true);
    try {
      await executeAndSettle(request);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          {backButton}
          <h1 className="text-2xl font-semibold text-foreground">
            {t("admin.approvals.detail.title")}
          </h1>
        </div>
        <div className="flex flex-wrap gap-2">
          {isPending ? (
            <RequireRole
              roles={[FLASHPAY_ROLES.SUPERADMIN, FLASHPAY_ROLES.ADMIN]}
            >
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex">
                      <Button
                        onClick={() => setApproveOpen(true)}
                        disabled={busy || selfApproval}
                        aria-disabled={busy || selfApproval}
                      >
                        <CheckCircle2 className="mr-2 h-4 w-4" />
                        {t("admin.approvals.decide.approve")}
                      </Button>
                    </span>
                  </TooltipTrigger>
                  {selfApproval ? (
                    <TooltipContent className="max-w-xs">
                      {t("admin.approvals.decide.selfApprovalTooltip")}
                    </TooltipContent>
                  ) : null}
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex">
                      <Button
                        variant="destructive"
                        onClick={() => setRejectOpen(true)}
                        disabled={busy || selfApproval}
                        aria-disabled={busy || selfApproval}
                      >
                        <XCircle className="mr-2 h-4 w-4" />
                        {t("admin.approvals.decide.reject")}
                      </Button>
                    </span>
                  </TooltipTrigger>
                  {selfApproval ? (
                    <TooltipContent className="max-w-xs">
                      {t("admin.approvals.decide.selfApprovalTooltip")}
                    </TooltipContent>
                  ) : null}
                </Tooltip>
              </TooltipProvider>
            </RequireRole>
          ) : null}
          {isPending && selfApproval ? (
            <Button
              variant="outline"
              onClick={() => setCancelOpen(true)}
              disabled={busy || cancel.isPending}
            >
              {cancel.isPending ? <ButtonSpinner /> : null}
              {t("admin.approvals.actions.cancel")}
            </Button>
          ) : null}
          {executionPending ? (
            <RequireRole
              roles={[
                FLASHPAY_ROLES.SUPERADMIN,
                FLASHPAY_ROLES.ADMIN,
                FLASHPAY_ROLES.SERVICE,
              ]}
            >
              <Button
                variant="outline"
                onClick={() => setRetryOpen(true)}
                disabled={busy}
              >
                {busy ? <ButtonSpinner /> : <Play className="mr-2 h-4 w-4" />}
                {t("admin.approvals.decide.execute")}
              </Button>
            </RequireRole>
          ) : null}
        </div>
      </div>

      <ApprovalRequestDetailCard approvalRequest={request} />

      <ApprovalRequestDecidePreview
        approvalRequest={request}
        open={approveOpen}
        onOpenChange={setApproveOpen}
        onConfirm={() => void handleApprove()}
        pending={busy}
        selfApproval={selfApproval}
      />
      <ApprovalRequestRejectDialog
        open={rejectOpen}
        onOpenChange={setRejectOpen}
        onSubmit={handleReject}
        pending={reject.isPending}
      />
      <MutationConfirmPreview
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        data={{
          id: request.id,
          actionType: request.actionType,
          resourceId: request.resourceId,
          status: request.status,
        }}
        title={t("common.mutationPreview.title")}
        description={t("common.mutationPreview.description")}
        confirmLabel={t("admin.approvals.actions.cancel")}
        t={t}
        pending={cancel.isPending}
        onConfirm={handleCancel}
      />
      <MutationConfirmPreview
        open={retryOpen}
        onOpenChange={setRetryOpen}
        data={{
          id: request.id,
          actionType: request.actionType,
          resourceId: request.resourceId,
          status: request.status,
        }}
        title={t("admin.approvals.decide.execute")}
        description={t("admin.approvals.decide.approveDescription")}
        confirmLabel={t("admin.approvals.decide.execute")}
        t={t}
        pending={busy}
        onConfirm={() => {
          void handleRetryExecution().then(() => setRetryOpen(false));
        }}
      />
    </section>
  );
}
